import { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, X, Search, Loader2, Brain, Link2, Edit2, Check, BookOpen, Sparkles, AlertTriangle, CheckCircle, CheckCircle2, HelpCircle, Info, ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import type { LearningOutcome, ESCOSkillMatch, BloomAnalysis, OutcomeESCOMapping } from '@/types';

// Bloom's Taxonomy levels in Norwegian
const BLOOM_LEVELS = {
  1: { name: 'Huske', color: 'bg-red-100 text-red-800 border-red-200' },
  2: { name: 'Forstå', color: 'bg-kf-orange-100 text-kf-orange-800 border-kf-orange-200' },
  3: { name: 'Anvende', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  4: { name: 'Analysere', color: 'bg-green-100 text-green-800 border-green-200' },
  5: { name: 'Evaluere', color: 'bg-kf-blue-100 text-kf-blue-800 border-kf-blue-200' },
  6: { name: 'Skape', color: 'bg-purple-100 text-purple-800 border-purple-200' },
};

// ESCO skill type colors
const SKILL_TYPE_COLORS = {
  'skill/competence': 'bg-kf-blue-50 text-kf-blue-700 border-kf-blue-200',
  'knowledge': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'transversal': 'bg-violet-50 text-violet-700 border-violet-200',
};

// ESCO v2: Confidence tier colors and labels
// 2026-07-01: Hevet terskler for å redusere falske positive i attester.
// Studieplaner for korte kurs eller privatpersoner fikk tidligere
// automatisk grønne ESCO-koblinger på 72% (f.eks. mikrofinans på et
// 4-timers privatøkonomi-kurs) som er for tekniske / nisje-orienterte.
const CONFIDENCE_TIERS = {
  high: {
    min: 0.90,
    label: 'Sterk match',
    color: 'bg-green-100 text-green-700 border-green-300',
    icon: CheckCircle,
  },
  medium: {
    min: 0.65,
    label: 'Mulig match',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    icon: AlertTriangle,
  },
  low: {
    min: 0.45,
    label: 'Svak match',
    color: 'bg-gray-100 text-gray-600 border-gray-300',
    icon: HelpCircle,
  },
};

// Get confidence tier for a score
const getConfidenceTier = (score: number): keyof typeof CONFIDENCE_TIERS | null => {
  if (score >= CONFIDENCE_TIERS.high.min) return 'high';
  if (score >= CONFIDENCE_TIERS.medium.min) return 'medium';
  if (score >= CONFIDENCE_TIERS.low.min) return 'low';
  return null;
};

// ESCO v2.2: Filter out unconfirmed medium/low tier matches for saving
// High tier (≥90%) matches are auto-accepted (hevet fra 70% i 2026-07-01)
// Medium (65-89%) and low (45-64%) matches require user confirmation
export const filterConfirmedESCOSkills = (skill: ESCOSkillMatch): boolean => {
  const confidence = skill.score || skill.confidence || 0;
  const tier = getConfidenceTier(confidence);
  // High tier is always included
  if (tier === 'high') return true;
  // Medium/low tier requires explicit confirmation
  return skill.confirmed === true;
};

// ESCO v2.2: Filter mappings to only include confirmed skills
export const filterConfirmedESCOMappings = (mappings: OutcomeESCOMapping[]): OutcomeESCOMapping[] => {
  return mappings
    .map(mapping => ({
      ...mapping,
      escoSkills: mapping.escoSkills.filter(filterConfirmedESCOSkills),
    }))
    .filter(mapping => mapping.escoSkills.length > 0);
};

// Quality issue type from backend
interface QualityIssue {
  type: string;
  severity: 'info' | 'warning' | 'error';
  message: string;
  suggestion?: string;
}

// Quality analysis result
interface QualityAnalysis {
  text: string;
  issues: QualityIssue[];
  hasWarnings: boolean;
  hasErrors: boolean;
  qualityScore: number;
}

interface CategoryWarning {
  outcome_category: 'knowledge' | 'skills' | 'competence';
  detected_better_pillar: string;
  top_matched_score: number;
  top_opposite_score: number;
  message: string;
  opposite_pillar_examples?: Array<{
    title: string;
    uri: string;
    skill_type: string;
    score: number;
  }>;
}

interface LearningOutcomeEditorProps {
  learning_outcomes: LearningOutcome;
  onChange: (outcomes: LearningOutcome) => void;
  /** Pre-existing ESCO mappings from database */
  escoMappings?: OutcomeESCOMapping[];
  /** Callback when ESCO mappings change */
  onESCOMappingsChange?: (mappings: OutcomeESCOMapping[]) => void;
  /** v6: Kategoriadvarsler per utbytte (key: "knowledge-0", "skills-1", etc.) */
  categoryWarnings?: Record<string, CategoryWarning>;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Show auto-suggest button */
  showAutoSuggest?: boolean;
  /** Label prefix for different contexts */
  labelPrefix?: string;
  /** EQF level for complexity validation */
  eqfLevel?: number;
  /** ECTS credits for complexity validation */
  ectsCredits?: number;
  /** Task #335 (2026-07-03): estimert timer for scope-basert ESCO-terskel.
      Micro-kurs (<15 t) skjuler forslag under 0.60 (istedet for 0.45)
      fordi ESCO er profesjons-fokusert og gir svake, villedende matches
      for allmenn-nivå tema som privatøkonomi og livskompetanse. */
  estimatedHours?: number;
  /** Study plan context (title + description) for better ESCO matching */
  studyPlanContext?: string;
  /** Study plan ID for feedback logging */
  studyPlanId?: string;
  /** ESCO matching profile: 'conservative' | 'balanced' | 'exploratory' */
  escoProfile?: 'conservative' | 'balanced' | 'exploratory';
  /** v6: ESCO-yrkesrolle-URIer (target_group + admission) for skill-relevansboost */
  occupationUris?: string[];
  /** Show quality warnings for learning outcomes */
  showQualityWarnings?: boolean;
}

export default function LearningOutcomeEditor({
  learning_outcomes,
  onChange,
  escoMappings = [],
  onESCOMappingsChange,
  categoryWarnings = {},
  disabled = false,
  showAutoSuggest = true,
  labelPrefix = '',
  eqfLevel,
  ectsCredits,
  estimatedHours: _estimatedHours,  // Task #335: reservert for fremtidig scope-basert terskel her
  studyPlanContext = '',
  studyPlanId,
  escoProfile = 'balanced',
  showQualityWarnings = true,
  occupationUris = [],
}: LearningOutcomeEditorProps) {
  // Input states
  const [knowledgeInput, setKnowledgeInput] = useState('');
  const [skillsInput, setSkillsInput] = useState('');
  const [competenceInput, setCompetenceInput] = useState('');

  // Edit states
  const [editingItem, setEditingItem] = useState<{
    type: 'knowledge' | 'skills' | 'competence';
    index: number;
    value: string;
  } | null>(null);

  // ESCO search state
  const [searchingESCO, setSearchingESCO] = useState<{
    type: 'knowledge' | 'skills' | 'competence';
    index: number;
  } | null>(null);
  const [escoSearchQuery, setEscoSearchQuery] = useState('');
  const [escoSearchResults, setEscoSearchResults] = useState<ESCOSkillMatch[]>([]);
  const [searchingInProgress, setSearchingInProgress] = useState(false);

  // Task #360: narrower-ekspansjon per søkeresultat
  const [expandedRelated, setExpandedRelated] = useState<Record<string, {
    loading: boolean;
    broader: Array<{ uri: string; title: string; description?: string; skill_type?: string }>;
    narrower: Array<{ uri: string; title: string; description?: string; skill_type?: string }>;
  } | undefined>>({});

  const toggleRelated = async (skillUri: string) => {
    if (expandedRelated[skillUri]) {
      setExpandedRelated(prev => {
        const next = { ...prev };
        delete next[skillUri];
        return next;
      });
      return;
    }
    setExpandedRelated(prev => ({ ...prev, [skillUri]: { loading: true, broader: [], narrower: [] } }));
    try {
      const res = await authenticatedFetch(`/api/esco/local/skills/${encodeURIComponent(skillUri)}/related`);
      if (res.ok) {
        const data = await res.json();
        setExpandedRelated(prev => ({
          ...prev,
          [skillUri]: {
            loading: false,
            broader: data.broader || [],
            narrower: data.narrower || []
          }
        }));
      } else {
        setExpandedRelated(prev => ({ ...prev, [skillUri]: { loading: false, broader: [], narrower: [] } }));
      }
    } catch (_err) {
      setExpandedRelated(prev => ({ ...prev, [skillUri]: { loading: false, broader: [], narrower: [] } }));
    }
  };

  // Auto-suggest state
  const [autoSuggesting, setAutoSuggesting] = useState(false);
  const [autoSuggestError, setAutoSuggestError] = useState('');
  const [showAutoSuggestConfirm, setShowAutoSuggestConfirm] = useState(false);
  const [aiSearchFallbackWarning, setAiSearchFallbackWarning] = useState<string | null>(null);

  // Bloom analysis cache (per outcome)
  const [bloomAnalysisCache, setBloomAnalysisCache] = useState<Record<string, BloomAnalysis>>({});
  const [analyzingBloom, setAnalyzingBloom] = useState<Set<string>>(new Set());

  // Local ESCO mappings state
  const [localMappings, setLocalMappings] = useState<OutcomeESCOMapping[]>(escoMappings);

  // ESCO v2: Quality analysis cache (per outcome)
  const [qualityAnalysisCache, setQualityAnalysisCache] = useState<Record<string, QualityAnalysis>>({});
  const [analyzingQuality, setAnalyzingQuality] = useState<Set<string>>(new Set());

  // ESCO v3.2: Description visibility state (default: expanded)
  const [showAllDescriptions, setShowAllDescriptions] = useState(true);
  // Task #328 (2026-07-03): kollapsibel forklaring av ESCO-farger og terskler
  const [showEscoHelp, setShowEscoHelp] = useState(false);
  const [expandedSkills, setExpandedSkills] = useState<Set<string>>(new Set());

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const qualityDebounceRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Update local mappings when prop changes
  useEffect(() => {
    setLocalMappings(escoMappings);
  }, [escoMappings]);

  // Notify parent of mappings changes.
  // VIKTIG: onESCOMappingsChange og escoMappings er BEVISST utelatt fra
  // dependency-array. Hvis vi inkluderer dem, vil en parent-render skape
  // ny referanse på onESCOMappingsChange-callbacken, useEffect kjører,
  // localMappings (gammel) !== escoMappings (ny prop), så vi kaller
  // onESCOMappingsChange(gammel) → setter gammel state tilbake → loop.
  // Vi skal kun kalle parent når VÅRE egne lokale endringer skjer.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (onESCOMappingsChange && localMappings !== escoMappings) {
      onESCOMappingsChange(localMappings);
    }
  }, [localMappings]);

  // Add a learning outcome item
  const addItem = (type: 'knowledge' | 'skills' | 'competence', value: string) => {
    if (!value.trim() || disabled) return;

    const updated = { ...learning_outcomes };
    updated[type] = [...(updated[type] || []), value.trim()];
    onChange(updated);

    // Clear the input
    if (type === 'knowledge') setKnowledgeInput('');
    else if (type === 'skills') setSkillsInput('');
    else setCompetenceInput('');

    // Analyze Bloom's taxonomy for the new item
    analyzeBloom(type, updated[type]!.length - 1, value.trim());
  };

  // Remove a learning outcome item
  const removeItem = (type: 'knowledge' | 'skills' | 'competence', index: number) => {
    if (disabled) return;

    const updated = { ...learning_outcomes };
    updated[type] = (updated[type] || []).filter((_, i) => i !== index);
    onChange(updated);

    // Remove associated ESCO mappings
    setLocalMappings(prev =>
      prev.filter(m => !(m.outcomeType === type && m.outcomeIndex === index))
    );
  };

  // Update a learning outcome item
  const updateItem = (type: 'knowledge' | 'skills' | 'competence', index: number, value: string) => {
    if (!value.trim() || disabled) return;

    const updated = { ...learning_outcomes };
    updated[type] = (updated[type] || []).map((item, i) => (i === index ? value.trim() : item));
    onChange(updated);
    setEditingItem(null);

    // Re-analyze Bloom's taxonomy
    analyzeBloom(type, index, value.trim());
  };

  // Analyze Bloom's taxonomy for an outcome
  const analyzeBloom = useCallback(async (
    type: 'knowledge' | 'skills' | 'competence',
    index: number,
    text: string
  ) => {
    const cacheKey = `${type}-${index}`;

    // Skip if already analyzing
    if (analyzingBloom.has(cacheKey)) return;

    setAnalyzingBloom(prev => new Set(prev).add(cacheKey));

    try {
      const response = await authenticatedFetch('/api/esco/local/detect-bloom', {
        method: 'POST',
        body: JSON.stringify({ text }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.bloom) {
          setBloomAnalysisCache(prev => ({
            ...prev,
            [cacheKey]: data.bloom,
          }));
        }
      }
    } catch (err) {
      console.error('Failed to analyze Bloom level:', err);
    } finally {
      setAnalyzingBloom(prev => {
        const next = new Set(prev);
        next.delete(cacheKey);
        return next;
      });
    }
  }, []);

  // ESCO v2: Analyze quality of a learning outcome
  const analyzeQuality = useCallback(async (
    type: 'knowledge' | 'skills' | 'competence',
    index: number,
    text: string
  ) => {
    const cacheKey = `${type}-${index}`;

    // Skip short text or if already analyzing
    if (text.length < 10 || analyzingQuality.has(cacheKey)) return;

    // Clear existing timer for this key
    if (qualityDebounceRef.current[cacheKey]) {
      clearTimeout(qualityDebounceRef.current[cacheKey]);
    }

    // Debounce the analysis
    qualityDebounceRef.current[cacheKey] = setTimeout(async () => {
      setAnalyzingQuality(prev => new Set(prev).add(cacheKey));

      try {
        const response = await authenticatedFetch('/api/esco/analyze-outcome', {
          method: 'POST',
          body: JSON.stringify({ outcome_text: text }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setQualityAnalysisCache(prev => ({
              ...prev,
              [cacheKey]: {
                text: data.text,
                issues: data.issues || [],
                hasWarnings: data.hasWarnings || false,
                hasErrors: data.hasErrors || false,
                qualityScore: data.qualityScore || 100,
              },
            }));
          }
        }
      } catch (err) {
        console.error('Failed to analyze quality:', err);
      } finally {
        setAnalyzingQuality(prev => {
          const next = new Set(prev);
          next.delete(cacheKey);
          return next;
        });
      }
    }, 500);
  }, []);

  // ESCO v3.1: Log feedback when accepting/rejecting ESCO matches
  // Feeds into the feedback loop to improve future matching
  const logESCOFeedback = useCallback(async (
    outcomeText: string,
    outcomeType: 'knowledge' | 'skills' | 'competence',
    skill: ESCOSkillMatch,
    action: 'accepted' | 'rejected' | 'replaced',
    suggestionRank?: number,
    replacementSkill?: ESCOSkillMatch
  ) => {
    try {
      await authenticatedFetch('/api/esco/local/feedback', {
        method: 'POST',
        body: JSON.stringify({
          // v3.1 API format
          outcomeText: outcomeText,
          outcomeType: outcomeType,
          escoUri: skill.uri,
          escoTitle: skill.title,
          escoSkillType: skill.skillType,
          action: action,
          originalConfidence: skill.score || skill.confidence,
          originalRank: suggestionRank,
          matchMethod: skill.matchMethod || escoProfile,
          replacedByUri: replacementSkill?.uri,
          replacedByTitle: replacementSkill?.title,
          studyPlanId: studyPlanId,
        }),
      });
    } catch (err) {
      // Silent fail - feedback logging should not break the UI
      console.warn('Failed to log ESCO feedback:', err);
    }
  }, [studyPlanId, escoProfile]);

  // Search ESCO skills
  const searchESCO = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setEscoSearchResults([]);
      return;
    }

    setSearchingInProgress(true);

    try {
      const response = await authenticatedFetch(
        `/api/esco/local/skills/search?q=${encodeURIComponent(query)}&language=no&limit=10`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.skills) {
          setEscoSearchResults(data.skills.map((s: any) => ({
            uri: s.uri,
            code: s.code,
            title: s.title,
            description: s.description,
            skillType: s.skill_type,
            score: s.relevance,
            title_lang: s.title_lang,
            description_lang: s.description_lang,
          })));
        }
      }
    } catch (err) {
      console.error('ESCO search failed:', err);
      setEscoSearchResults([]);
    } finally {
      setSearchingInProgress(false);
    }
  }, []);

  // Debounced search
  const handleESCOSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setEscoSearchQuery(query);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      searchESCO(query);
    }, 300);
  };

  // Add ESCO skill to outcome
  const addESCOSkillToOutcome = (
    type: 'knowledge' | 'skills' | 'competence',
    index: number,
    skill: ESCOSkillMatch,
    suggestionRank?: number
  ) => {
    // Get outcome text for feedback logging
    const outcomeText = (learning_outcomes[type] || [])[index] || '';

    setLocalMappings(prev => {
      const existing = prev.find(
        m => m.outcomeType === type && m.outcomeIndex === index
      );

      if (existing) {
        // Check if skill already added
        if (existing.escoSkills.some(s => s.uri === skill.uri)) {
          return prev;
        }
        // Add to existing mapping
        return prev.map(m =>
          m.outcomeType === type && m.outcomeIndex === index
            ? { ...m, escoSkills: [...m.escoSkills, skill] }
            : m
        );
      }

      // Create new mapping
      return [
        ...prev,
        {
          outcomeIndex: index,
          outcomeType: type,
          escoSkills: [skill],
          bloomAnalysis: bloomAnalysisCache[`${type}-${index}`],
        },
      ];
    });

    // ESCO v2: Log feedback when accepting a skill
    if (outcomeText) {
      logESCOFeedback(outcomeText, type, skill, 'accepted', suggestionRank);
    }

    // Clear search
    setEscoSearchQuery('');
    setEscoSearchResults([]);
    setSearchingESCO(null);
  };

  // Remove ESCO skill from outcome
  const removeESCOSkillFromOutcome = (
    type: 'knowledge' | 'skills' | 'competence',
    index: number,
    skillUri: string
  ) => {
    if (disabled) return;

    // Get outcome text and skill for feedback logging
    const outcomeText = (learning_outcomes[type] || [])[index] || '';
    const mapping = localMappings.find(m => m.outcomeType === type && m.outcomeIndex === index);
    const skill = mapping?.escoSkills.find(s => s.uri === skillUri);

    setLocalMappings(prev =>
      prev
        .map(m => {
          if (m.outcomeType === type && m.outcomeIndex === index) {
            return {
              ...m,
              escoSkills: m.escoSkills.filter(s => s.uri !== skillUri),
            };
          }
          return m;
        })
        .filter(m => m.escoSkills.length > 0)
    );

    // ESCO v2: Log feedback when removing/rejecting a skill
    if (outcomeText && skill) {
      logESCOFeedback(outcomeText, type, skill, 'rejected');
    }
  };

  // ESCO v2.2: Toggle confirmation for medium/low tier skills
  const toggleESCOSkillConfirmation = (
    type: 'knowledge' | 'skills' | 'competence',
    index: number,
    skillUri: string
  ) => {
    if (disabled) return;

    setLocalMappings(prev =>
      prev.map(m => {
        if (m.outcomeType === type && m.outcomeIndex === index) {
          return {
            ...m,
            escoSkills: m.escoSkills.map(s => {
              if (s.uri === skillUri) {
                return { ...s, confirmed: !s.confirmed };
              }
              return s;
            }),
          };
        }
        return m;
      })
    );
  };

  // ESCO v3.2: Toggle individual skill description visibility
  const toggleSkillDescription = (skillUri: string) => {
    setExpandedSkills(prev => {
      const next = new Set(prev);
      if (next.has(skillUri)) {
        next.delete(skillUri);
      } else {
        next.add(skillUri);
      }
      return next;
    });
  };

  // ESCO v3.2: Toggle all descriptions
  const toggleAllDescriptions = () => {
    if (showAllDescriptions) {
      // Hide all
      setExpandedSkills(new Set());
      setShowAllDescriptions(false);
    } else {
      // Show all - collect all skill URIs from current mappings
      const allUris = new Set<string>();
      localMappings.forEach(m => {
        m.escoSkills.forEach(s => allUris.add(s.uri));
      });
      setExpandedSkills(allUris);
      setShowAllDescriptions(true);
    }
  };

  // Handler for auto-suggest button click - shows confirmation if existing mappings
  const handleAutoSuggestClick = () => {
    if (disabled) return;

    // Check if there are existing ESCO mappings
    const hasExistingMappings = localMappings.some(m => m.escoSkills && m.escoSkills.length > 0);

    if (hasExistingMappings) {
      // Show confirmation dialog
      setShowAutoSuggestConfirm(true);
    } else {
      // No existing mappings, run directly
      autoSuggestESCO();
    }
  };

  // Auto-suggest ESCO skills for all outcomes
  const autoSuggestESCO = async () => {
    if (disabled) return;
    setShowAutoSuggestConfirm(false); // Close confirmation dialog if open

    setAutoSuggesting(true);
    setAutoSuggestError('');
    setAiSearchFallbackWarning(null); // v3: Reset AI fallback warning

    try {
      // Collect all learning outcomes
      const allOutcomes: { type: 'knowledge' | 'skills' | 'competence'; index: number; text: string }[] = [];

      (['knowledge', 'skills', 'competence'] as const).forEach(type => {
        (learning_outcomes[type] || []).forEach((text, index) => {
          allOutcomes.push({ type, index, text });
        });
      });

      if (allOutcomes.length === 0) {
        setAutoSuggestError('Ingen læringsutbytter å analysere');
        return;
      }

      // Build the learning_outcomes structure for the API
      const outcomesForApi = {
        knowledge: learning_outcomes.knowledge || [],
        skills: learning_outcomes.skills || [],
        competence: learning_outcomes.competence || [],
      };

      // v3: Bygg full studieplan-kontekst for GPT re-ranking
      const fullStudyPlanContext = {
        title: studyPlanContext?.split(' - ')[0] || '',
        description: studyPlanContext?.split(' - ').slice(1).join(' - ') || studyPlanContext || '',
        eqfLevel: eqfLevel,
        ectsCredits: ectsCredits,
        estimatedHours: ectsCredits ? ectsCredits * 25 : undefined,
      };

      const response = await authenticatedFetch('/api/esco/local/match-outcomes', {
        method: 'POST',
        body: JSON.stringify({
          learning_outcomes: outcomesForApi,
          context: studyPlanContext, // Legacy: Studieplan-kontekst for domenematch
          profile: escoProfile, // ESCO v2: A/B-testbar profil
          studyPlanContext: fullStudyPlanContext, // v3: Full kontekst for GPT re-ranking
          occupation_uris: occupationUris, // v6: Yrkesrolle-boost (target_group + admission)
        }),
      });

      if (!response.ok) {
        throw new Error('Kunne ikke hente ESCO-forslag');
      }

      const data = await response.json();

      // v3: Sjekk om AI-fallback ble brukt og vis varsling
      if (data.aiSearchStatus?.usedAiFallback && data.aiSearchStatus?.message) {
        setAiSearchFallbackWarning(data.aiSearchStatus.message);
      }

      if (data.success && data.results) {
        const newMappings: OutcomeESCOMapping[] = [];

        // Process each category
        (['knowledge', 'skills', 'competence'] as const).forEach(type => {
          const categoryResults = data.results[type];
          if (categoryResults && Array.isArray(categoryResults)) {
            categoryResults.forEach((result: any, index: number) => {
              // Backend returns esco_skills, not matches
              const escoSkills = result.esco_skills || result.matches || [];
              if (escoSkills.length > 0) {
                newMappings.push({
                  outcomeIndex: index,
                  outcomeType: type,
                  escoSkills: escoSkills.map((m: any) => ({
                    uri: m.uri,
                    code: m.code,
                    title: m.title,
                    description: m.description,
                    skillType: m.skill_type,
                    score: m.confidence || m.score,
                    confidence: m.confidence || m.score, // v3: Alias for score
                    matchedVerb: m.matched_verb,
                    // v3: GPT re-ranking metadata
                    gptScore: m.gptScore,
                    gptReasoning: m.gptReasoning,
                    matchMethod: m.matchMethod,
                    // v5.15: språk-flagg for varsel
                    title_lang: m.title_lang,
                    description_lang: m.description_lang,
                  })),
                  // Backend returns bloom_level, bloom_verb, bloom_name separately
                  bloomAnalysis: result.bloom || (result.bloom_level ? {
                    level: result.bloom_level,
                    levelName: result.bloom_name,
                    matchedVerb: result.bloom_verb,
                  } : undefined),
                });
              }

              // Update Bloom cache - handle both formats
              const bloomData = result.bloom || (result.bloom_level ? {
                level: result.bloom_level,
                levelName: result.bloom_name,
                matchedVerb: result.bloom_verb,
              } : null);

              if (bloomData) {
                setBloomAnalysisCache(prev => ({
                  ...prev,
                  [`${type}-${index}`]: bloomData,
                }));
              }
            });
          }
        });

        setLocalMappings(newMappings);
      }
    } catch (err: any) {
      console.error('Auto-suggest failed:', err);
      setAutoSuggestError(err.message || 'Kunne ikke hente ESCO-forslag');
    } finally {
      setAutoSuggesting(false);
    }
  };

  // Analyze Bloom's taxonomy for all outcomes on mount
  useEffect(() => {
    (['knowledge', 'skills', 'competence'] as const).forEach(type => {
      (learning_outcomes[type] || []).forEach((text, index) => {
        const cacheKey = `${type}-${index}`;
        if (!bloomAnalysisCache[cacheKey] && !analyzingBloom.has(cacheKey)) {
          analyzeBloom(type, index, text);
        }
      });
    });
  }, [learning_outcomes, bloomAnalysisCache, analyzingBloom, analyzeBloom]);

  // ESCO v2: Analyze quality for all outcomes
  useEffect(() => {
    if (!showQualityWarnings) return;

    (['knowledge', 'skills', 'competence'] as const).forEach(type => {
      (learning_outcomes[type] || []).forEach((text, index) => {
        const cacheKey = `${type}-${index}`;
        if (!qualityAnalysisCache[cacheKey] && !analyzingQuality.has(cacheKey) && text.length >= 10) {
          analyzeQuality(type, index, text);
        }
      });
    });
  }, [learning_outcomes, qualityAnalysisCache, analyzingQuality, analyzeQuality, showQualityWarnings]);

  // Get ESCO mappings for an outcome
  const getOutcomeMappings = (type: 'knowledge' | 'skills' | 'competence', index: number) => {
    return localMappings.find(m => m.outcomeType === type && m.outcomeIndex === index);
  };

  // Render a single outcome item with ESCO badges
  const renderOutcomeItem = (
    type: 'knowledge' | 'skills' | 'competence',
    item: string,
    index: number
  ) => {
    const cacheKey = `${type}-${index}`;
    const bloomAnalysis = bloomAnalysisCache[cacheKey];
    const mapping = getOutcomeMappings(type, index);
    const isSearching = searchingESCO?.type === type && searchingESCO?.index === index;
    const isEditing = editingItem?.type === type && editingItem?.index === index;
    const isAnalyzingBloom = analyzingBloom.has(cacheKey);

    return (
      <div key={`${type}-${index}`} className="group relative">
        {/* Main item row */}
        <div className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
          {/* Bloom's Taxonomy indicator */}
          <div className="flex-shrink-0 w-20">
            {isAnalyzingBloom ? (
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <Loader2 size={12} className="animate-spin" />
              </div>
            ) : bloomAnalysis ? (
              <div
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
                  BLOOM_LEVELS[bloomAnalysis.level as keyof typeof BLOOM_LEVELS]?.color || 'bg-gray-100 text-gray-600'
                }`}
                title={`Bloom nivå ${bloomAnalysis.level}: ${bloomAnalysis.levelName} (${bloomAnalysis.matchedVerb})`}
              >
                <Brain size={10} />
                <span>{bloomAnalysis.level}</span>
              </div>
            ) : (
              <div className="w-8" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editingItem.value}
                  onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })}
                  className="flex-1 input text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      updateItem(type, index, editingItem.value);
                    } else if (e.key === 'Escape') {
                      setEditingItem(null);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => updateItem(type, index, editingItem.value)}
                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                >
                  <Check size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setEditingItem(null)}
                  className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <span className="text-sm text-gray-900">{item}</span>
            )}

            {/* ESCO Skills badges with confidence tiers (v2), confirmation (v2.2), and expandable descriptions (v3.2) */}
            {mapping && mapping.escoSkills.length > 0 && !isEditing && (
              <div className="flex flex-col gap-1.5 mt-2">
                {mapping.escoSkills.map((skill) => {
                  // Get confidence tier for visual styling
                  const confidence = skill.score || skill.confidence || 0;
                  const tier = getConfidenceTier(confidence);
                  const tierConfig = tier ? CONFIDENCE_TIERS[tier] : null;
                  const TierIcon = tierConfig?.icon || Link2;

                  // ESCO v2.2: Determine if confirmation is needed (medium/low tier)
                  const needsConfirmation = tier === 'medium' || tier === 'low';
                  const isConfirmed = skill.confirmed === true;

                  // Style: confirmed medium/low gets green border, unconfirmed stays yellow/gray
                  const baseColor = tierConfig?.color ||
                    SKILL_TYPE_COLORS[skill.skillType as keyof typeof SKILL_TYPE_COLORS] ||
                    'bg-gray-50 text-gray-600 border-gray-200';
                  const confirmedColor = needsConfirmation && isConfirmed
                    ? 'bg-green-50 text-green-700 border-green-300'
                    : baseColor;

                  // ESCO v3.2: Check if description is expanded
                  const isExpanded = expandedSkills.has(skill.uri) || showAllDescriptions;
                  const hasDescription = skill.description && skill.description.length > 0;

                  return (
                    <div key={skill.uri} className="flex flex-col">
                      <div
                        className={`flex items-start gap-2 px-2.5 py-1.5 rounded-t text-xs border ${confirmedColor} ${!isExpanded || !hasDescription ? 'rounded-b' : 'border-b-0'}`}
                        title={!hasDescription ? skill.title : undefined}
                      >
                        {/* Expand/collapse toggle for description */}
                        {hasDescription ? (
                          <button
                            type="button"
                            onClick={() => toggleSkillDescription(skill.uri)}
                            className="flex-shrink-0 mt-0.5 hover:opacity-70"
                            title={isExpanded ? 'Skjul beskrivelse' : 'Vis beskrivelse'}
                          >
                            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          </button>
                        ) : (
                          <TierIcon size={12} className="flex-shrink-0 mt-0.5" />
                        )}
                        <button
                          type="button"
                          onClick={() => hasDescription && toggleSkillDescription(skill.uri)}
                          className={`flex-1 text-left ${hasDescription ? 'cursor-pointer hover:underline' : ''}`}
                          title={hasDescription ? `${skill.title}\n\n${skill.description}` : skill.title}
                        >
                          {skill.title}
                        </button>
                        {/* EN-badge: ESCO mangler norsk oversettelse for tittel eller beskrivelse */}
                        {((skill as any).title_lang === 'en' || (skill as any).description_lang === 'en') && (
                          <span
                            className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300"
                            title={
                              (skill as any).title_lang === 'en' && (skill as any).description_lang === 'en'
                                ? 'ESCO mangler norsk oversettelse for både tittel og beskrivelse. Viser engelsk.'
                                : (skill as any).title_lang === 'en'
                                  ? 'ESCO mangler norsk tittel. Viser engelsk.'
                                  : 'ESCO mangler norsk beskrivelse. Tittel er norsk, beskrivelsen er engelsk.'
                            }
                          >
                            EN
                          </span>
                        )}
                        {/* Confidence badge */}
                        {tierConfig && (
                          <span className="flex-shrink-0 text-[10px] opacity-75">
                            {Math.min(100, Math.round(confidence * 100))}%
                          </span>
                        )}
                        {/* ESCO v2.2: Confirmation button for medium/low tier */}
                        {!disabled && needsConfirmation && (
                          <button
                            type="button"
                            onClick={() => toggleESCOSkillConfirmation(type, index, skill.uri)}
                            className={`flex-shrink-0 ml-1 ${isConfirmed ? 'text-green-600' : 'text-gray-400 hover:text-green-600'}`}
                            title={isConfirmed ? 'Kobling godkjent (klikk for å angre)' : 'Godkjenn denne koblingen'}
                          >
                            {isConfirmed ? <CheckCircle size={12} /> : <CheckCircle2 size={12} />}
                          </button>
                        )}
                        {!disabled && (
                          <button
                            type="button"
                            onClick={() => removeESCOSkillFromOutcome(type, index, skill.uri)}
                            className="flex-shrink-0 ml-1 hover:text-red-600"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                      {/* ESCO v3.2: Expandable description */}
                      {isExpanded && hasDescription && (
                        <div className={`px-3 py-2 text-xs text-gray-600 bg-gray-50 border border-t-0 rounded-b ${confirmedColor.includes('green') ? 'border-green-300' : confirmedColor.includes('yellow') ? 'border-yellow-300' : 'border-gray-200'}`}>
                          {skill.description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ESCO v2: Quality warnings */}
            {showQualityWarnings && !isEditing && qualityAnalysisCache[cacheKey]?.issues?.length > 0 && (
              <div className="mt-2 space-y-1">
                {qualityAnalysisCache[cacheKey].issues.map((issue, issueIndex) => (
                  <div
                    key={`${cacheKey}-issue-${issueIndex}`}
                    className={`flex items-start gap-2 px-2.5 py-1.5 rounded text-xs border ${
                      issue.severity === 'error'
                        ? 'bg-red-50 text-red-700 border-red-200'
                        : issue.severity === 'warning'
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-kf-blue-50 text-kf-blue-700 border-kf-blue-200'
                    }`}
                  >
                    {issue.severity === 'error' ? (
                      <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                    ) : issue.severity === 'warning' ? (
                      <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                    ) : (
                      <Info size={12} className="flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <span>{issue.message}</span>
                      {issue.suggestion && (
                        <span className="block text-[10px] opacity-75 mt-0.5">
                          Forslag: {issue.suggestion}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* v6: Kategoriadvarsel — utbyttet matcher sterkere mot opposite ESCO-pillar */}
            {!isEditing && categoryWarnings[cacheKey] && (
              <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-md text-xs border bg-kf-orange-50 text-kf-orange-800 border-kf-orange-300">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-kf-orange-600" />
                <div className="flex-1 space-y-1">
                  <div className="font-medium">Mulig feilkategorisering</div>
                  <div>{categoryWarnings[cacheKey].message}</div>
                  {categoryWarnings[cacheKey].opposite_pillar_examples && categoryWarnings[cacheKey].opposite_pillar_examples.length > 0 && (
                    <div className="text-[11px] opacity-90 mt-1">
                      <span className="font-medium">ESCO-treff i {categoryWarnings[cacheKey].detected_better_pillar === 'knowledge' ? 'kunnskap' : 'ferdighet/kompetanse'}:</span>{' '}
                      {categoryWarnings[cacheKey].opposite_pillar_examples.slice(0, 3).map((ex: any) => ex.title).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ESCO Search panel */}
            {isSearching && (
              <div className="mt-2 p-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="flex items-center gap-2">
                  <Search size={14} className="text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={escoSearchQuery}
                    onChange={handleESCOSearchChange}
                    placeholder="Søk etter ESCO-ferdighet..."
                    className="flex-1 text-sm border-0 focus:ring-0 p-0"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setSearchingESCO(null);
                        setEscoSearchQuery('');
                        setEscoSearchResults([]);
                      }
                    }}
                  />
                  {searchingInProgress && <Loader2 size={14} className="animate-spin text-gray-400" />}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchingESCO(null);
                      setEscoSearchQuery('');
                      setEscoSearchResults([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={14} />
                  </button>
                </div>

                {escoSearchResults.length > 0 && (
                  <div className="mt-2 max-h-64 overflow-y-auto">
                    {escoSearchResults.map((skill) => {
                      // Task #358: norsk oversettelse + fargekode av ESCO skill_type
                      const skillTypeInfo: Record<string, { label: string; className: string; tooltip: string }> = {
                        'knowledge': {
                          label: 'Kunnskap',
                          className: 'bg-kf-blue-100 text-kf-blue-800 border-kf-blue-300',
                          tooltip: 'ESCO-type: knowledge (kunnskap). Kognitiv forståelse — hva/hvorfor.'
                        },
                        'skill/competence': {
                          label: 'Ferdighet/kompetanse',
                          className: 'bg-green-100 text-green-800 border-green-300',
                          tooltip: 'ESCO-type: skill/competence. Anvendt ferdighet med kompetansepreg.'
                        },
                        'skill': {
                          label: 'Ferdighet',
                          className: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                          tooltip: 'ESCO-type: skill. Ren praktisk ferdighet — hvordan noe gjøres.'
                        }
                      };
                      const info = skill.skillType ? skillTypeInfo[skill.skillType] : null;
                      // Advars indikator når treffet er i "feil" pillar for outcome-type
                      const outcomeIsKnowledge = type === 'knowledge';
                      const outcomeIsSkill = type === 'skills';
                      const treffIsKnowledge = skill.skillType === 'knowledge';
                      const treffIsSkill = skill.skillType === 'skill' || skill.skillType === 'skill/competence';
                      const crossPillar = (outcomeIsKnowledge && treffIsSkill) || (outcomeIsSkill && treffIsKnowledge);

                      return (
                      <button
                        key={skill.uri}
                        type="button"
                        onClick={() => addESCOSkillToOutcome(type, index, skill)}
                        className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm border-b border-gray-100 last:border-0"
                      >
                        <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap" title={skill.title}>
                          <span>{skill.title}</span>
                          {info && (
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${info.className}`}
                              title={info.tooltip}
                            >
                              {info.label}
                            </span>
                          )}
                          {((skill as any).title_lang === 'en' || (skill as any).description_lang === 'en') && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300"
                              title={
                                (skill as any).title_lang === 'en'
                                  ? 'ESCO mangler norsk oversettelse. Viser engelsk.'
                                  : 'Tittel er norsk, men ESCO mangler norsk beskrivelse.'
                              }
                            >
                              EN
                            </span>
                          )}
                        </div>
                        {crossPillar && (
                          <div className="mt-1 text-[11px] text-kf-orange-700 bg-kf-orange-50 border border-kf-orange-200 rounded px-2 py-1">
                            {outcomeIsKnowledge
                              ? 'Merk: dette er en ferdighet i ESCO, ikke ren kunnskap. Grensen er pedagogisk flytende — bekreft valget.'
                              : 'Merk: dette er kunnskap i ESCO, ikke en ren ferdighet. Grensen er pedagogisk flytende — bekreft valget.'}
                          </div>
                        )}
                        {skill.description && (
                          <div className="text-xs text-gray-600 mt-1 whitespace-pre-line">{skill.description}</div>
                        )}
                        {/* Task #360: Se relaterte i ESCO-hierarki */}
                        <div className="mt-2 flex items-center gap-2">
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); toggleRelated(skill.uri); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); toggleRelated(skill.uri); } }}
                            className="text-[11px] text-kf-primary hover:underline cursor-pointer inline-flex items-center gap-1"
                            title="Vis foreldre- og barn-ferdigheter i ESCO-hierarkiet"
                          >
                            {expandedRelated[skill.uri] ? '▼' : '▶'} Relaterte i hierarki
                          </span>
                        </div>
                        {expandedRelated[skill.uri] && (
                          <div className="mt-2 pl-3 border-l-2 border-gray-200 space-y-2" onClick={(e) => e.stopPropagation()}>
                            {expandedRelated[skill.uri]!.loading ? (
                              <div className="text-[11px] text-gray-500 italic">Henter...</div>
                            ) : (
                              <>
                                {expandedRelated[skill.uri]!.broader.length > 0 && (
                                  <div>
                                    <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1">Bredere (forelder)</div>
                                    <ul className="text-[11px] text-gray-700 space-y-0.5">
                                      {expandedRelated[skill.uri]!.broader.map(r => (
                                        <li key={r.uri} title={r.description || ''}>↑ {r.title}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {expandedRelated[skill.uri]!.narrower.length > 0 && (
                                  <div>
                                    <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1">Mer spesifikke (barn)</div>
                                    <ul className="text-[11px] text-gray-700 space-y-0.5 max-h-32 overflow-y-auto">
                                      {expandedRelated[skill.uri]!.narrower.map(r => (
                                        <li key={r.uri} title={r.description || ''}>↓ {r.title}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {expandedRelated[skill.uri]!.broader.length === 0 && expandedRelated[skill.uri]!.narrower.length === 0 && (
                                  <div className="text-[11px] text-gray-500 italic">Ingen relaterte i ESCO-hierarkiet.</div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </button>
                      );
                    })}
                  </div>
                )}

                {escoSearchQuery.length >= 2 && !searchingInProgress && escoSearchResults.length === 0 && (
                  <div className="mt-2 text-sm text-gray-500 text-center py-2">
                    Ingen resultater for "{escoSearchQuery}"
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          {!isEditing && !disabled && (
            <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => setSearchingESCO(isSearching ? null : { type, index })}
                className={`p-1 rounded ${isSearching ? 'bg-kf-primary text-white' : 'text-gray-400 hover:bg-gray-100'}`}
                title="Legg til ESCO-ferdighet"
              >
                <Link2 size={14} />
              </button>
              <button
                type="button"
                onClick={() => setEditingItem({ type, index, value: item })}
                className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                title="Rediger"
              >
                <Edit2 size={14} />
              </button>
              <button
                type="button"
                onClick={() => removeItem(type, index)}
                className="p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 rounded"
                title="Slett"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render outcome category
  const renderCategory = (
    type: 'knowledge' | 'skills' | 'competence',
    label: string,
    icon: React.ReactNode,
    items: string[],
    input: string,
    setInput: (val: string) => void,
    placeholder: string
  ) => {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            {icon}
            {labelPrefix ? `${labelPrefix} - ` : ''}{label}
            <span className="text-gray-400 font-normal">({items.length})</span>
          </label>
        </div>

        {/* Items list */}
        <div className="space-y-1.5">
          {items.map((item, index) => renderOutcomeItem(type, item, index))}
        </div>

        {/* Add new input */}
        {!disabled && (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={placeholder}
              className="flex-1 input text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addItem(type, input);
                }
              }}
            />
            <button
              type="button"
              onClick={() => addItem(type, input)}
              disabled={!input.trim()}
              className="btn btn-secondary p-2"
            >
              <Plus size={16} />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with auto-suggest and description toggle */}
      {showAutoSuggest && !disabled && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Læringsutbytter med ESCO-kobling og Bloom-analyse
          </div>
          <div className="flex items-center gap-2">
            {/* ESCO v3.2: Toggle all descriptions button */}
            {localMappings.some(m => m.escoSkills?.some(s => s.description)) && (
              <button
                type="button"
                onClick={toggleAllDescriptions}
                className="btn btn-ghost text-sm flex items-center gap-2 text-gray-600 hover:text-gray-800"
                title={showAllDescriptions ? 'Skjul alle ESCO-beskrivelser' : 'Vis alle ESCO-beskrivelser'}
              >
                {showAllDescriptions ? (
                  <>
                    <EyeOff size={14} />
                    <span className="hidden sm:inline">Skjul beskrivelser</span>
                  </>
                ) : (
                  <>
                    <Eye size={14} />
                    <span className="hidden sm:inline">Vis beskrivelser</span>
                  </>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={handleAutoSuggestClick}
              disabled={autoSuggesting || (!learning_outcomes.knowledge?.length && !learning_outcomes.skills?.length && !learning_outcomes.competence?.length)}
              className="btn btn-secondary text-sm flex items-center gap-2"
            >
              {autoSuggesting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Analyserer...
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Auto-foreslå ESCO
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Task #328 (2026-07-03): Kompakt forklaring av ESCO-fargekoder og terskler.
          Reduserer forvirring om at gule/grå bokser er FORSLAG, ikke automatiske
          koblinger. Kollapsibel så den ikke tar plass etter man har lest den. */}
      {showAutoSuggest && !disabled && (
        <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg">
          <button
            type="button"
            onClick={() => setShowEscoHelp(!showEscoHelp)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm text-kf-blue-900 hover:bg-kf-blue-100 rounded-lg"
          >
            <span className="flex items-center gap-2">
              <Info size={14} />
              <span className="font-medium">Hvordan tolke ESCO-forslagene?</span>
            </span>
            {showEscoHelp ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {showEscoHelp && (
            <div className="px-3 pb-3 text-xs text-gray-700 space-y-2 border-t border-kf-blue-200 pt-3">
              <p>
                ESCO-ferdigheter foreslås basert på semantisk sammenlikning mellom læringsutbytte og EU-standardens ferdighetskatalog. Fargen viser hvor sikker matchen er:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="flex items-start gap-2 bg-green-100 border border-green-300 rounded p-2">
                  <CheckCircle size={14} className="text-green-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-green-800">Sterk match — ≥ 90 %</div>
                    <div className="text-green-700">Auto-koblet. Fjern om irrelevant.</div>
                  </div>
                </div>
                <div className="flex items-start gap-2 bg-yellow-100 border border-yellow-300 rounded p-2">
                  <AlertTriangle size={14} className="text-yellow-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-yellow-800">Mulig match — 65–89 %</div>
                    <div className="text-yellow-700">Forslag. Klikk for å bekrefte.</div>
                  </div>
                </div>
                <div className="flex items-start gap-2 bg-gray-100 border border-gray-300 rounded p-2">
                  <HelpCircle size={14} className="text-gray-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-700">Svak match — 45–64 %</div>
                    <div className="text-gray-600">Forslag. Sjelden relevant.</div>
                  </div>
                </div>
              </div>
              <p className="pt-1">
                <strong>Under 45 %:</strong> vises ikke — for lav treffsikkerhet.
                {' '}<strong>Manuelt søk:</strong> bruk lenken <em>«Legg til ESCO-ferdighet»</em> under hvert læringsutbytte for å søke opp og koble en spesifikk ferdighet direkte.
              </p>
              <p className="text-gray-600 italic">
                Kun grønne (og bekreftede gule/grå) lagres med studieplanen. Ubekreftede forslag rensker seg selv når du forlater siden.
              </p>
            </div>
          )}
        </div>
      )}

      {autoSuggestError && (
        <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
          {autoSuggestError}
        </div>
      )}

      {/* v3: AI fallback warning */}
      {aiSearchFallbackWarning && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            <span>{aiSearchFallbackWarning}</span>
            <button
              onClick={() => setAiSearchFallbackWarning(null)}
              className="ml-2 text-amber-600 hover:text-amber-800 underline"
            >
              Lukk
            </button>
          </div>
        </div>
      )}

      {/* Complexity warning based on EQF/ECTS */}
      {eqfLevel && ectsCredits && (
        <ComplexityWarning
          bloomAnalyses={Object.values(bloomAnalysisCache)}
          eqfLevel={eqfLevel}
          ectsCredits={ectsCredits}
        />
      )}

      {/* Knowledge */}
      {renderCategory(
        'knowledge',
        'Kunnskap',
        <BookOpen size={16} className="text-emerald-600" />,
        learning_outcomes.knowledge || [],
        knowledgeInput,
        setKnowledgeInput,
        'Legg til kunnskapsmål...'
      )}

      {/* Skills */}
      {renderCategory(
        'skills',
        'Ferdigheter',
        <Brain size={16} className="text-kf-blue-600" />,
        learning_outcomes.skills || [],
        skillsInput,
        setSkillsInput,
        'Legg til ferdighetsmål...'
      )}

      {/* Competence */}
      {renderCategory(
        'competence',
        'Generell kompetanse',
        <Sparkles size={16} className="text-purple-600" />,
        learning_outcomes.competence || [],
        competenceInput,
        setCompetenceInput,
        'Legg til kompetansemål...'
      )}

      {/* Auto-suggest confirmation modal */}
      {showAutoSuggestConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Erstatte eksisterende ESCO-koblinger?</h2>
              <p className="text-gray-600">
                Du har allerede ESCO-koblinger på noen læringsutbytter. Auto-foreslå vil erstatte alle eksisterende koblinger med nye forslag.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  Endringene lagres ikke før du trykker <strong>Lagre utkast</strong>. Du kan angre ved å laste siden på nytt uten å lagre.
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={autoSuggestESCO}
                className="flex-1 bg-kf-accent text-kf-dark py-2 rounded-lg hover:bg-kf-accent-80 transition font-medium"
              >
                Erstatt koblinger
              </button>
              <button
                onClick={() => setShowAutoSuggestConfirm(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Complexity warning component
function ComplexityWarning({
  bloomAnalyses,
  eqfLevel,
  ectsCredits,
}: {
  bloomAnalyses: BloomAnalysis[];
  eqfLevel: number;
  ectsCredits: number;
}) {
  if (bloomAnalyses.length === 0) return null;

  // Calculate average Bloom level
  const avgBloom = bloomAnalyses.reduce((sum, b) => sum + b.level, 0) / bloomAnalyses.length;

  // Expected minimum Bloom level based on EQF
  const expectedMinBloom = Math.max(1, Math.min(6, Math.floor(eqfLevel / 1.5)));

  // Check for issues
  const warnings: string[] = [];

  if (avgBloom < expectedMinBloom - 0.5) {
    warnings.push(
      `Gjennomsnittlig Bloom-nivå (${avgBloom.toFixed(1)}) er lavt for EQF-nivå ${eqfLevel}. Vurder å inkludere mer avanserte læringsutbytter.`
    );
  }

  // Check if all outcomes are at low levels for higher ECTS
  if (ectsCredits >= 5 && bloomAnalyses.every(b => b.level <= 2)) {
    warnings.push(
      `Alle læringsutbytter er på grunnleggende nivå (Huske/Forstå). For ${ectsCredits} ECTS bør det inkluderes mer avanserte læringsmål.`
    );
  }

  if (warnings.length === 0) return null;

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
      <div className="flex items-start gap-2">
        <Brain className="text-yellow-600 flex-shrink-0 mt-0.5" size={16} />
        <div className="text-sm text-yellow-800">
          <div className="font-medium mb-1">Kompleksitetsadvarsel</div>
          <ul className="list-disc list-inside space-y-1">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// Types are exported from @/types
